// Stage 5 Phase-3 — Portal Overview rewrite.
//
// Two-column layout (desktop ≥ md):
//   • Left column (~75 % — `md:col-span-3`): project name, client
//     name, property address, project summary, thumbnail image.
//   • Right column (~25 % — `md:col-span-1`): stacked tiles —
//     Action Required · Current Workstage · Current Design ·
//     Upcoming Programme.  Mobile: stacks below the left column.
//
// Project name fallback: `project.project_name` ?? `client_name`.
// Resolution happens client-side so the admin drawer can clearly
// distinguish "custom name set" from "falling back to client name".
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { portalApi, fmtDate } from "./portalApi";
import { EmptyState, Spinner } from "./PortalUi";
import { usePageMeta } from "../../components/Layout";
import { API } from "../../lib/api";

export default function PortalOverview() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => {
        portalApi.overview()
            .then((r) => setData(r.data))
            .catch((e) => setErr(
                e?.response?.data?.detail || "Unavailable"));
    }, []);

    const projectName = resolveProjectName(data?.project);
    // Page title follows the resolved display name so the browser tab
    // reflects the admin-authored project_name when set.
    usePageMeta({
        title: projectName
            ? `${projectName} | Studio G4 Portal`
            : "Overview | Studio G4 Portal",
    });

    if (err) {
        return (
            <div className="py-8 px-4" data-testid="portal-overview-err">
                <EmptyState heading="Overview unavailable" note={err} />
            </div>
        );
    }
    if (!data) return <Spinner testid="portal-overview-loading" />;
    const p = data.project;

    return (
        <div className="py-8 px-4" data-testid="portal-overview">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* -------- LEFT COLUMN (75 %) ---------------------- */}
                <div className="md:col-span-3 flex flex-col gap-4">
                    <HeroBlock project={p} displayName={projectName} />
                </div>

                {/* -------- RIGHT COLUMN (25 %) --------------------- */}
                <aside className="md:col-span-1 flex flex-col gap-4"
                        data-testid="portal-overview-tiles">
                    <ActionRequiredTile
                        count={data.outstanding_actions_count || 0} />
                    <CurrentWorkstageTile
                        n={p.workstage}
                        label={p.workstage_label} />
                    <CurrentDesignTile issue={data.current_design_issue} />
                    <UpcomingProgrammeTile
                        items={data.programme_snapshot || []} />
                </aside>
            </div>
        </div>
    );
}


// ---------------------------------------------------------------------
// Fallback resolver — project_name ?? client_name.  Both sides now
// render the exact string the admin drawer last saved; when the
// override is unset the portal falls through to the canonical
// two-client display name computed server-side.
// ---------------------------------------------------------------------
function resolveProjectName(project) {
    if (!project) return "";
    const custom = (project.project_name || "").trim();
    if (custom) return custom;
    return (project.client_name || "").trim();
}


// ---------------------------------------------------------------------
// Left column — hero block (name, client, address, summary, image).
// Each sub-row is independently conditional; nothing renders empty.
// ---------------------------------------------------------------------
function HeroBlock({ project, displayName }) {
    const addressLine = (project.property_address || "").trim()
        || (project.postcode || "").trim();
    const summary = (project.project_summary || "").trim();
    const thumb = project.project_thumbnail || null;
    // A1 — hide the duplicate client-name row when it reads identically
    // to the resolved display name (the common case while
    // `project_name` is still unset and the fallback renders the
    // client name as the heading).  Strict trim-equality — nulls /
    // empties do not match, so the row still renders when it carries
    // information the heading does not.
    const dn = (displayName || "").trim();
    const cn = (project.client_name || "").trim();
    const showClientRow = cn && cn !== dn;
    return (
        <section data-testid="portal-overview-hero"
                  className="flex flex-col gap-3">
            {/* A3 — H1 styling mirrors the shared PortalUi `PageHeader`
                convention (`text-[26px] md:text-[30px] font-manrope
                font-semibold leading-tight`).  Inlined rather than
                reusing <PageHeader /> because the overview hero
                carries additional body-copy rows beneath the title
                that <PageHeader /> does not express. */}
            <h1 className="font-manrope text-[26px] md:text-[30px] font-semibold text-charcoal leading-tight break-words"
                 data-testid="portal-overview-project-name">
                {displayName || "Your project"}
            </h1>
            {showClientRow && (
                <div className="text-[15px] text-[#4A4A46]"
                      data-testid="portal-overview-client-name">
                    {project.client_name}
                </div>
            )}
            {addressLine && (
                <div className="text-[14px] text-[#6B6B67]"
                      data-testid="portal-overview-address">
                    {addressLine}
                </div>
            )}
            {summary && (
                <p className="mt-2 text-[14px] leading-relaxed text-charcoal whitespace-pre-wrap max-w-[64ch]"
                    data-testid="portal-overview-summary">
                    {summary}
                </p>
            )}
            {thumb && (
                <div className="mt-3 w-full max-w-[500px] aspect-square overflow-hidden rounded-lg border border-[#E5E2DC] bg-[#F5F3EE]"
                      data-testid="portal-overview-thumbnail-wrap">
                    <img
                        src={`${API}/portal/project-thumbnail`}
                        alt={displayName || "Project thumbnail"}
                        data-testid="portal-overview-thumbnail"
                        className="w-full h-full object-cover" />
                </div>
            )}
        </section>
    );
}


// ---------------------------------------------------------------------
// Right-column tiles — each is a small Card.  TileFrame centralises
// the shared chrome so additions land consistently.
// ---------------------------------------------------------------------
function TileFrame({ label, testid, children }) {
    return (
        <div className="rounded-lg border border-[#E5E2DC] bg-white p-4"
             data-testid={testid}>
            <h2 className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-manrope font-semibold mb-2">
                {label}
            </h2>
            {children}
        </div>
    );
}


function ActionRequiredTile({ count }) {
    return (
        <TileFrame label="Action required"
                     testid="portal-overview-actions-card">
            <div className="font-manrope text-[32px] font-semibold text-charcoal leading-none"
                  data-testid="portal-overview-outstanding">
                {count}
            </div>
            <div className="mt-1.5 text-[12px] text-[#6B6B67]">
                Items awaiting your input
            </div>
            {count > 0 && (
                <Link to="/portal/actions"
                       data-testid="portal-overview-goto-actions"
                       className="mt-2 inline-block text-[12px] text-[#A8543F] font-manrope font-semibold hover:underline">
                    View actions →
                </Link>
            )}
        </TileFrame>
    );
}


function CurrentWorkstageTile({ n, label }) {
    const num = Number.isFinite(n) ? n : null;
    return (
        <TileFrame label="Current workstage"
                     testid="portal-overview-workstage-card">
            <div className="font-manrope text-[18px] font-semibold text-charcoal leading-tight"
                  data-testid="portal-overview-workstage-n">
                {num ? `Workstage ${num}` : "—"}
            </div>
            {label && (
                <div className="mt-1 text-[13px] text-[#4A4A46]"
                      data-testid="portal-overview-workstage-label">
                    {label}
                </div>
            )}
        </TileFrame>
    );
}


function CurrentDesignTile({ issue }) {
    return (
        <TileFrame label="Current design"
                     testid="portal-overview-design-card">
            {issue ? (
                <>
                    <div className="font-manrope text-[14px] font-semibold text-charcoal leading-tight">
                        {issue.title}
                    </div>
                    <div className="mt-1 text-[12px] text-[#6B6B67]">
                        Feedback due {fmtDate(issue.feedback_due_date)}
                    </div>
                    <Link to="/portal/design"
                           data-testid="portal-overview-goto-design"
                           className="mt-2 inline-block text-[12px] text-[#A8543F] font-manrope font-semibold hover:underline">
                        View design →
                    </Link>
                </>
            ) : (
                <div className="text-[13px] text-[#6B6B67]">
                    No current design issue.
                </div>
            )}
        </TileFrame>
    );
}


function UpcomingProgrammeTile({ items }) {
    return (
        <TileFrame label="Upcoming programme"
                     testid="portal-overview-programme">
            {items.length === 0 ? (
                <div className="text-[13px] text-[#6B6B67]">
                    No upcoming items.
                </div>
            ) : (
                <ul className="divide-y divide-[#EEEBE4]">
                    {items.slice(0, 5).map((it) => (
                        <li key={it.id}
                             className="py-1.5"
                             data-testid={`portal-overview-prog-${it.id}`}>
                            <div className="text-[13px] text-charcoal leading-tight">
                                {it.title}
                            </div>
                            <div className="text-[11px] text-[#8A8A84] mt-0.5">
                                {fmtDate(it.target_at)}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
            <Link to="/portal/programme"
                   data-testid="portal-overview-goto-programme"
                   className="mt-3 inline-block text-[12px] text-[#A8543F] font-manrope font-semibold hover:underline">
                Full programme →
            </Link>
        </TileFrame>
    );
}
